import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { 
  getAllFactures, 
  createFacture,
  updateFacture,
  deleteFacture as deleteFactureAPI,
  annulerFacture,
  formatMontant, 
  getSymboleMonnaie,
  getStatutClass, 
  mapStatutForDisplay,
  formatDate,
  canEditFacture 
} from '../services/facturesAPI';
import { getAvoirByFacture } from '../services/avoirsAPI';
import { openAvoirPdfInNewTab } from '../utils/pdf/avoirPdf';
import '../styles/Facturation.css';
import FactureForm from './FactureForm';
import ClientAutocomplete from './ClientAutocomplete'; // 🔴 NOUVEAU : Import du composant autocomplete
import { useSettings } from '../contexts/SettingsContext'; // 🔴 NOUVEAU : Import du contexte des paramètres
import settingsService from '../services/settingsService'; // 🔴 NOUVEAU : Import du service des paramètres
import PortalDropdown from './PortalDropdown';
import { getAppDateInputValue } from '../utils/dateUtils';
import { drawModernInvoiceTemplate } from '../utils/pdf/invoiceTemplate';

const getInvoiceDisplayNumber = (facture) => (
  facture?.numero || facture?.numero_facture || 'Non attribu\u00e9'
);

const getInvoicePdfFileName = (facture) => {
  const numero = facture?.numero || facture?.numero_facture;
  return numero
    ? `Facture-Officielle-${numero}.pdf`
    : `Brouillon-facture-${facture?.id}.pdf`;
};

// Injecter les animations CSS pour le modal de succès
if (!document.getElementById('success-modal-animations')) {
  const style = document.createElement('style');
  style.id = 'success-modal-animations';
  style.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slideInScale {
      from {
        transform: scale(0.7) translateY(-50px);
        opacity: 0;
      }
      to {
        transform: scale(1) translateY(0);
        opacity: 1;
      }
    }
    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.05);
        opacity: 0.8;
      }
    }
  `;
  document.head.appendChild(style);
}

// 🔴 FONCTIONS POUR GESTION TTC
// Calculer le montant HT à partir du montant TTC selon la monnaie
const calculerMontantHT = (montantTTC, monnaie = 'MAD') => {
  const ttc = parseFloat(montantTTC) || 0;
  let ht;
  
  if (monnaie === 'CAD') {
    // TVA Canada : 14,975%
    ht = ttc / 1.14975; // Montant HT = TTC / 1.14975
  } else {
    // TVA Maroc : 20%
    ht = ttc / 1.2; // Montant HT = TTC / 1.2
  }
  
  return Math.round(ht * 100) / 100; // Arrondir à 2 décimales
};

// Calculer la TVA à partir du montant TTC selon la monnaie
const calculerMontantTVA = (montantTTC, monnaie = 'MAD') => {
  const ttc = parseFloat(montantTTC) || 0;
  const ht = calculerMontantHT(montantTTC, monnaie);
  const tva = ttc - ht;
  return Math.round(tva * 100) / 100; // Arrondir à 2 décimales
};

// 🔴 FONCTION POUR DÉTECTER LES FACTURES EN RETARD
const estFactureEnRetard = (facture) => {
  // Si le backend fournit déjà la propriété en_retard, l'utiliser
  if (typeof facture.en_retard === 'boolean') {
    return facture.en_retard;
  }
  
  // Sinon, calculer côté client (fallback)
  // Une facture est en retard si :
  // 1. Elle n'est pas payée (statut différent de 'payee')
  // 2. Sa date d'échéance est dépassée de plus de 30 jours
  if (facture.statut?.toLowerCase() === 'payee' || facture.statut?.toLowerCase() === 'annulee') {
    return false;
  }
  
  if (!facture.dateEcheance) {
    return false;
  }
  
  const dateEcheance = new Date(facture.dateEcheance);
  const maintenant = new Date();
  const diffEnJours = Math.floor((maintenant - dateEcheance) / (1000 * 60 * 60 * 24));
  
  return diffEnJours > 30;
};

// Formater un montant TTC en affichant les détails HT et TVA
const formatMontantDetaille = (montantTTC, monnaie = 'MAD') => {
  const ttc = parseFloat(montantTTC) || 0;
  return formatMontant(ttc, monnaie);
};

// Formater pour affichage avec détail HT et TVA
const formatMontantAvecDetails = (montantTTC, monnaie = 'MAD') => {
  const ttc = parseFloat(montantTTC) || 0;
  const ht = calculerMontantHT(montantTTC, monnaie);
  const tva = calculerMontantTVA(montantTTC, monnaie);
  const symbole = getSymboleMonnaie(monnaie);
  const tauxTVA = monnaie === 'CAD' ? '14,975%' : '20%';
  return `${ttc.toFixed(2)} ${symbole} TTC (HT: ${ht.toFixed(2)} ${symbole} + TVA ${tauxTVA}: ${tva.toFixed(2)} ${symbole})`;
};

function Facturation({ userRole: userRoleProp = 'conseillere', navigationParams = {} }) {
  const { settings } = useSettings(); // 🔴 NOUVEAU : Utilisation du contexte des paramètres
  // La secrétaire consulte toutes les factures, modifie les brouillons et peut
  // enregistrer un paiement, sans pouvoir créer ou annuler une facture.
  const isFacturationReadOnly = userRoleProp === 'secretaire';
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState(userRoleProp); // 🔴 NOUVEAU : État pour le rôle utilisateur
  const isSuperAdmin = String(userRole || userRoleProp || '').toLowerCase() === 'super_admin';
  const [filteredBy, setFilteredBy] = useState('');
  const [conseilleresListe, setConseilleresListe] = useState([]); // 🔴 NOUVEAU : Liste des conseillères

  const canMarkAsPaid = (statut) => {
    const normalizedStatus = String(statut || '').toLowerCase().replace(/\s+/g, '_');
    const normalizedRole = String(userRole || userRoleProp || '').toLowerCase();
    if (normalizedRole === 'secretaire') {
      return ['brouillon', 'payable', 'en_retard'].includes(normalizedStatus);
    }
    return canEditFacture(statut, normalizedRole)
      && !['payee', 'annulee'].includes(normalizedStatus);
  };
  
  // 🔴 NOUVEAUX ÉTATS POUR LE TRI
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const facturesPerPage = 10;
  
  const [filtres, setFiltres] = useState({
    statut: '',
    client: '',
    periode: '',
    conseillere: '' // 🔴 NOUVEAU : Filtre par conseillère
  });
  
  const [nouvelleFacture, setNouvelleFacture] = useState({
    client: '',
    selectedClientId: null, // 🔴 NOUVEAU : ID du client sélectionné
    montant: '',
    monnaie: 'MAD', // 🔴 NOUVEAU : Monnaie par défaut MAD
    dateEmission: getAppDateInputValue(),
    dateEcheance: '',
    datePaiement: '',
    description: ''
  });
  
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [factureDetails, setFactureDetails] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [factureToEdit, setFactureToEdit] = useState(null);
  
  // État pour le modal de succès moderne
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({ numero: '', montant: '', monnaie: 'MAD' });
  
  // État pour le modal d'erreur de l'avoir
  const [showAvoirErrorModal, setShowAvoirErrorModal] = useState(false);
  
  // État pour gérer l'ouverture/fermeture des menus Actions (portalisé comme Leads/Clients)
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  
  // Options pour les filtres et le formulaire
  const statutOptions = ['Brouillon', 'Payable', 'Payée', 'En retard', 'Annulée'];
  const periodeOptions = ['Ce mois', 'Mois précédent', 'Ce trimestre', 'Cette année'];
  const methodePaiementOptions = ['Carte de crédit', 'Virement bancaire', 'Chèque', 'Espèces', 'PayPal'];

  // 🔴 FONCTION DE TRI
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    console.log(`🔄 Tri par ${key} en ordre ${direction}`);
  };

  // 🔴 FONCTION POUR TRIER LES DONNÉES
  const getSortedFactures = (factures) => {
    if (!sortConfig.key) {
      return factures;
    }

    return [...factures].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Gestion spéciale pour les différents types de données
      switch(sortConfig.key) {
        case 'numero':
          // Tri alphanumérique pour les numéros de facture
          aValue = aValue || '';
          bValue = bValue || '';
          break;
        
        case 'montant':
          // Tri numérique pour les montants
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
          break;
        
        case 'dateEmission':
        case 'dateEcheance':
          // Tri par date
          aValue = new Date(aValue || '1900-01-01');
          bValue = new Date(bValue || '1900-01-01');
          break;
        
        case 'statut':
          // Tri par statut avec ordre logique
          const statutOrder = {
            'brouillon': 0,
            'payable': 1,
            'en_retard': 2,
            'payee': 3,
            'annulee': 4
          };
          aValue = statutOrder[aValue?.toLowerCase()] ?? 999;
          bValue = statutOrder[bValue?.toLowerCase()] ?? 999;
          break;
        
        case 'client':
        default:
          // Tri alphabétique pour texte
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
          break;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // 🔴 FONCTION POUR AFFICHER L'ICÔNE DE TRI
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-icon" title="Cliquer pour trier">⇵</span>; // Icône neutre
    }
    return (
      <span className="sort-icon active" title={`Trié par ${columnKey} (${sortConfig.direction === 'asc' ? 'croissant' : 'décroissant'})`}>
        {sortConfig.direction === 'asc' ? '▴' : '▾'}
      </span>
    );
  };

  // Fonctions pour la pagination
  const getTotalPages = () => {
    const filteredFactures = getFilteredFactures();
    const totalPages = Math.ceil(filteredFactures.length / facturesPerPage);
    console.log(`📊 Pagination Debug Factures - Total factures filtrées: ${filteredFactures.length}, Factures per page: ${facturesPerPage}, Total pages: ${totalPages}`);
    return totalPages;
  };

  const getPaginatedFactures = () => {
    const sortedFactures = getSortedFactures(getFilteredFactures());
    const startIndex = (currentPage - 1) * facturesPerPage;
    const endIndex = startIndex + facturesPerPage;
    const paginated = sortedFactures.slice(startIndex, endIndex);
    console.log(`📄 Page ${currentPage}: Affichage ${startIndex + 1} à ${Math.min(endIndex, sortedFactures.length)} sur ${sortedFactures.length} factures`);
    return paginated;
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < getTotalPages()) {
      goToPage(currentPage + 1);
    }
  };
  
  // Charger les factures depuis l'API
  const chargerFactures = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await getAllFactures();
      
      if (result.success) {
        setFactures(result.data);
        setFilteredBy(result.filteredBy || 'Toutes');
        console.log('✅ Factures chargées:', result.data.length, 'factures');
        console.log('🔍 Filtrées par:', result.filteredBy || 'Toutes');
        console.log('👤 Utilisateur connecté:', userName, '- Rôle:', userRole);
        
        // 🔴 NOUVEAU : Extraire la liste unique des conseillères depuis les factures
        const conseilleresUniques = [...new Set(
          result.data
            .map(f => f.validePar)
            .filter(v => v && v.trim() !== '')
        )].sort();
        setConseilleresListe(conseilleresUniques);
        console.log('👥 Conseillères trouvées:', conseilleresUniques);
        
        // Debug: Afficher les factures et leur validePar pour comprendre le filtrage
        if (userRole === 'conseillere') {
          console.log('🔍 Détail des factures pour débogage:');
          result.data.forEach(facture => {
            console.log(`   Facture ${facture.numero}: validePar="${facture.validePar}"`);
          });
        }
        
        // Message informatif pour les conseillères
        if (userRole === 'conseillere' && result.filteredBy && result.filteredBy !== 'Toutes') {
          console.log('🔒 Affichage limité aux factures de la conseillère connectée');
        }
      } else {
        setError(result.error || 'Erreur lors du chargement des factures');
        console.error('❌ Erreur:', result.error);
      }
    } catch (error) {
      setError('Erreur de connexion à l\'API');
      console.error('❌ Erreur de connexion:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Charger les informations utilisateur au démarrage
  useEffect(() => {
    const storedUserName = localStorage.getItem('userName') || '';
    const storedRole = localStorage.getItem('role') || '';
    setUserName(storedUserName);
    setUserRole(storedRole); // 🔴 NOUVEAU : Stocker le rôle dans l'état
    console.log('👤 Utilisateur connecté:', storedUserName, '- Rôle:', storedRole);
  }, []);

  // Charger les factures au démarrage
  useEffect(() => {
    chargerFactures();
  }, []);

  // 🎯 Appliquer les filtres de navigation (ex: depuis le tableau de bord)
  useEffect(() => {
    if (navigationParams && navigationParams.filtreStatut) {
      console.log('📊 Application du filtre de navigation:', navigationParams.filtreStatut);
      setFiltres(prevFiltres => ({
        ...prevFiltres,
        statut: navigationParams.filtreStatut
      }));
    }
  }, [navigationParams]);
  
  // Obtenir la liste des clients depuis les factures
  const clientsOptions = Array.from(
    new Set(factures.map(f => f.client).filter(Boolean))
  ).sort();
  
  // Filtrer par période
  const filtrerParPeriode = (dateEmission, periode) => {
    // Si pas de période sélectionnée ou période vide, afficher toutes les factures
    if (!periode || periode === '' || periode === 'Tous' || periode === 'Toutes') {
      return true;
    }
    
    // Vérifier que la date d'émission est valide
    if (!dateEmission) {
      return false;
    }
    
    const date = new Date(dateEmission);
    const maintenant = new Date();
    
    // Vérifier que la date est valide
    if (isNaN(date.getTime())) {
      return false;
    }
    
    switch(periode) {
      case 'Ce mois':
        return date.getMonth() === maintenant.getMonth() && date.getFullYear() === maintenant.getFullYear();
      case 'Mois précédent':
        const moisPrecedent = new Date(maintenant.getFullYear(), maintenant.getMonth() - 1);
        return date.getMonth() === moisPrecedent.getMonth() && date.getFullYear() === moisPrecedent.getFullYear();
      case 'Ce trimestre':
        const trimestre = Math.floor(maintenant.getMonth() / 3);
        const trimestreDate = Math.floor(date.getMonth() / 3);
        return trimestreDate === trimestre && date.getFullYear() === maintenant.getFullYear();
      case 'Cette année':
        return date.getFullYear() === maintenant.getFullYear();
      default:
        return true;
    }
  };
  
  // Filtrer les factures
  const getFilteredFactures = () => {
    return factures.filter(facture => {
      const passStatut = filtres.statut === '' || facture.statut === filtres.statut;
      const passClient = filtres.client === '' || 
        facture.client?.toLowerCase().includes(filtres.client.toLowerCase());
      const passPeriode = filtres.periode === '' || filtrerParPeriode(facture.dateEmission, filtres.periode);
      const passConseillere = filtres.conseillere === '' || 
        facture.validePar?.toLowerCase().includes(filtres.conseillere.toLowerCase()); // 🔴 NOUVEAU : Filtre par conseillère
      
      // Debug pour le filtre de période
      if (filtres.periode !== '') {
        console.log(`🔍 Filtre période "${filtres.periode}" - Facture ${facture.numero}: dateEmission=${facture.dateEmission}, passe=${passPeriode}`);
      }
      
      // Debug pour le filtre client
      if (filtres.client !== '') {
        console.log(`🔍 Filtre client "${filtres.client}" - Facture ${facture.numero}: client="${facture.client}", passe=${passClient}`);
      }
      
      // Debug pour le filtre conseillère
      if (filtres.conseillere !== '') {
        console.log(`🔍 Filtre conseillère "${filtres.conseillere}" - Facture ${facture.numero}: validePar="${facture.validePar}", passe=${passConseillere}`);
      }
      
      return passStatut && passClient && passPeriode && passConseillere;
    });
  };
  
  // Pour compatibilité, garder facturesFiltrees comme alias
  const facturesFiltrees = getFilteredFactures();
  
  // Gérer les changements de filtres
  const handleFiltreChange = (e) => {
    const { name, value } = e.target;
    setFiltres({
      ...filtres,
      [name]: value
    });
    setCurrentPage(1); // Revenir à la première page lors du changement de filtre
  };
  
  // Réinitialiser les filtres
  const resetFiltres = () => {
    setFiltres({
      statut: '',
      client: '',
      periode: '',
      conseillere: '' // 🔴 NOUVEAU : Réinitialiser aussi le filtre conseillère
    });
    setCurrentPage(1); // Revenir à la première page
  };
  
  // Gérer les changements dans le formulaire de nouvelle facture
  const handleNouvelleFactureChange = (e) => {
    const { name, value } = e.target;
    setNouvelleFacture({
      ...nouvelleFacture,
      [name]: value
    });
  };
  
  // 🔴 NOUVEAU : Gérer la sélection d'un client depuis l'autocomplete
  const handleClientSelect = (selection) => {
    setNouvelleFacture({
      ...nouvelleFacture,
      client: selection.displayText || '',
      selectedClientId: selection.clientId || null
    });
  };
  
  // Calculer la date d'échéance (30 jours après émission)
  const calculerDateEcheance = (dateEmission) => {
    const date = new Date(dateEmission);
    date.setDate(date.getDate() + 30);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  // Mettre à jour la date d'échéance lorsque la date d'émission change
  const handleDateEmissionChange = (e) => {
    const dateEmission = e.target.value;
    const dateEcheance = calculerDateEcheance(dateEmission);
    
    setNouvelleFacture({
      ...nouvelleFacture,
      dateEmission,
      dateEcheance
    });
  };
  
  // Ajouter une nouvelle facture
  const ajouterFacture = async (e) => {
    e.preventDefault();
    
    // Validation des données
    if (!nouvelleFacture.selectedClientId || !nouvelleFacture.montant || !nouvelleFacture.description) {
      alert('Veuillez remplir tous les champs requis (client, montant, description)');
      return;
    }
    
    try {
      console.log('📝 Envoi des données de création de facture:', {
        client_id: nouvelleFacture.selectedClientId,
        montant: nouvelleFacture.montant,
        monnaie: nouvelleFacture.monnaie,
        description: nouvelleFacture.description,
        dateEmission: nouvelleFacture.dateEmission,
        dateEcheance: nouvelleFacture.dateEcheance,
        datePaiement: nouvelleFacture.datePaiement
      });
      
      // Créer la facture via l'API backend
      const response = await createFacture({
        client_id: nouvelleFacture.selectedClientId,
        montant: parseFloat(nouvelleFacture.montant),
        monnaie: nouvelleFacture.monnaie,
        description: nouvelleFacture.description,
        dateEmission: nouvelleFacture.dateEmission,
        dateEcheance: nouvelleFacture.dateEcheance,
        datePaiement: nouvelleFacture.datePaiement || null,
        statut: 'brouillon'
      });
      
      if (response.success) {
        console.log('✅ Facture créée avec succès:', response.data);
        
        // Recharger la liste des factures
        await chargerFactures();
        
        // Réinitialiser le formulaire
        setNouvelleFacture({
          client: '',
          selectedClientId: null,
          montant: '',
          monnaie: 'MAD',
          dateEmission: getAppDateInputValue(),
          dateEcheance: '',
          datePaiement: '',
          description: ''
        });
        
        setShowModal(false);
        alert('Facture créée avec succès !');
      } else {
        throw new Error(response.message || 'Erreur lors de la création de la facture');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la création de la facture:', error);
      alert('Erreur lors de la création de la facture: ' + error.message);
    }
  };
  
  // Voir les détails d'une facture
  const voirDetails = (facture) => {
    setFactureDetails(facture);
    setShowDetailsModal(true);
  };
  
  // Changer le statut d'une facture
  const changerStatut = async (id, nouveauStatut, methodePaiement = null, datePaiement = null) => {
    try {
      console.log('🔄 === DEBUT CHANGEMENT STATUT ===');
      console.log('🔄 ID facture:', id);
      console.log('🔄 Nouveau statut:', nouveauStatut);
      console.log('🔄 Méthode de paiement:', methodePaiement);
      
      // 🔴 CORRECTION: Normaliser les statuts français vers les statuts anglais pour l'API
      const statutMapping = {
        'Brouillon': 'brouillon',
        'Payable': 'payable', 
        'Payée': 'payee',        // ⭐ CORRECTION: "Payée" → "payee"
        'En retard': 'en_retard',
        'Annulée': 'annulee'
      };
      
      const statutNormalise = statutMapping[nouveauStatut] || nouveauStatut.toLowerCase();
      console.log('🔄 Statut normalisé pour l\'API:', statutNormalise);
      
      let result;
      
      // 🎯 IMPORTANT: Si on annule une facture, utiliser la route spéciale /annuler
      // qui créera automatiquement l'avoir
      if (nouveauStatut === 'Annulée') {
        console.log('🚫 Annulation de facture - Création automatique d\'avoir');
        result = await annulerFacture(id, 'Annulation par administrateur');
      } else {
        // Pour les autres statuts, utiliser la mise à jour normale
        const factureActuelle = factures.find((facture) => facture.id === id);
        const updateData = {
          statut: statutNormalise,
          datePaiement: nouveauStatut === 'Payée'
            ? (datePaiement || factureActuelle?.datePaiement || getAppDateInputValue())
            : null,
          methodePaiement: nouveauStatut === 'Payée' ? methodePaiement : null
        };
        
        console.log('📤 Données à envoyer à l\'API:', updateData);
        result = await updateFacture(id, updateData);
      }
      
      console.log('📥 Réponse de l\'API:', result);
      
      if (result.success) {
        // Recharger les factures pour avoir les données à jour
        await chargerFactures();
        
        // Fermer les modals et mettre à jour les détails si nécessaire
        if (showDetailsModal) {
          setShowDetailsModal(false);
          setFactureDetails(null);
        }
        
        console.log('✅ Statut de facture mis à jour:', nouveauStatut);
        
        // Message de succès avec design moderne
        const notification = document.createElement('div');
        
        // Déterminer l'icône et la couleur selon le statut
        let icon = '✅';
        let bgColor = '#10b981'; // Vert par défaut
        let message = '';
        
        switch(nouveauStatut) {
          case 'Payée':
            icon = '💰';
            bgColor = '#10b981'; // Vert
            message = methodePaiement 
              ? `Facture marquée comme payée (${methodePaiement})` 
              : 'Facture marquée comme payée';
            break;
          case 'En retard':
            icon = '⚠️';
            bgColor = '#f59e0b'; // Orange
            message = 'Facture marquée en retard';
            break;
          case 'Annulée':
            icon = '🚫';
            bgColor = '#ef4444'; // Rouge
            message = 'Facture annulée';
            break;
          case 'Payable':
            icon = '📄';
            bgColor = '#3b82f6'; // Bleu
            message = 'Facture rendue payable';
            break;
          default:
            icon = '✅';
            bgColor = '#10b981';
            message = `Facture ${nouveauStatut.toLowerCase()} avec succès`;
        }
        
        notification.innerHTML = `
          <div style="display: flex; align-items: center; gap: 12px;">
            <div style="
              font-size: 24px;
              background: rgba(255, 255, 255, 0.2);
              width: 40px;
              height: 40px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
            ">
              ${icon}
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 600; font-size: 15px; margin-bottom: 2px;">
                Succès !
              </div>
              <div style="font-size: 13px; opacity: 0.95;">
                ${message}
              </div>
            </div>
            <button onclick="this.parentElement.parentElement.remove()" style="
              background: transparent;
              border: none;
              color: white;
              font-size: 20px;
              cursor: pointer;
              padding: 0;
              width: 24px;
              height: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0.7;
              transition: opacity 0.2s;
            " onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.7'">
              ×
            </button>
          </div>
        `;
        
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: ${bgColor};
          color: white;
          padding: 16px 20px;
          border-radius: 12px;
          z-index: 9999;
          min-width: 320px;
          max-width: 420px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2);
          animation: slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          backdrop-filter: blur(10px);
        `;
        
        // Ajouter l'animation CSS si elle n'existe pas déjà
        if (!document.getElementById('notification-animation-style')) {
          const style = document.createElement('style');
          style.id = 'notification-animation-style';
          style.textContent = `
            @keyframes slideInRight {
              from {
                transform: translateX(400px);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
            @keyframes slideOutRight {
              from {
                transform: translateX(0);
                opacity: 1;
              }
              to {
                transform: translateX(400px);
                opacity: 0;
              }
            }
          `;
          document.head.appendChild(style);
        }
        
        document.body.appendChild(notification);
        
        // Animation de sortie avant suppression
        setTimeout(() => {
          notification.style.animation = 'slideOutRight 0.3s ease-out forwards';
          setTimeout(() => {
            if (notification.parentElement) {
              document.body.removeChild(notification);
            }
          }, 300);
        }, 4000);
        
      } else {
        console.error('❌ Erreur mise à jour:', result.error);
        const errorMessage = result.error || 'Erreur lors de la mise à jour du statut';
        setError(errorMessage);
        alert(`❌ Erreur: ${errorMessage}`);
      }
    } catch (error) {
      console.error('❌ === ERREUR CHANGEMENT STATUT ===');
      console.error('❌ Erreur complète:', error);
      console.error('❌ Message d\'erreur:', error.message);
      console.error('❌ Stack trace:', error.stack);
      
      const errorMessage = error.message || 'Erreur lors du changement de statut';
      setError(errorMessage);
      alert(`❌ Erreur technique: ${errorMessage}`);
    }
  };
  
  // Calculer les statistiques (montants déjà en TTC dans la base)
  const supprimerFacture = async (facture) => {
    if (!isSuperAdmin) return;

    const numero = facture.numero || facture.numero_facture || `brouillon #${facture.id}`;
    const confirmation = window.confirm(
      `Supprimer d\u00e9finitivement de l'affichage la facture ${numero} ?\n\nSon num\u00e9ro officiel restera r\u00e9serv\u00e9.`
    );
    if (!confirmation) return;

    try {
      await deleteFactureAPI(facture.id);
      setFactures((liste) => liste.filter((item) => item.id !== facture.id));
      setShowDetailsModal(false);
      setFactureDetails(null);
      setDropdownOpenId(null);
      setDropdownAnchorEl(null);
    } catch (error) {
      alert(error.message || 'Erreur lors de la suppression de la facture');
    }
  };

  const calculerStatistiques = () => {
    const totalFactures = factures.length;
    
    // 🔥 IMPORTANT: Les montants en base sont déjà TTC
    // On les traite directement sans conversion
    let totalMontantMAD = 0;
    let totalMontantCAD = 0;
    
    factures.forEach(facture => {
      const montantTTC = parseFloat(facture.montant || 0); // Déjà TTC en base
      const monnaie = facture.monnaie || 'MAD'; // Par défaut MAD si non spécifié
      
      if (monnaie === 'MAD') {
        totalMontantMAD += montantTTC;
      } else if (monnaie === 'CAD') {
        totalMontantCAD += montantTTC;
      }
    });
    
    // 🔥 Afficher le montant principal selon la monnaie dominante
    let montantPrincipal = totalMontantMAD;
    let monnaieAffichage = 'MAD';
    
    // Si les factures sont principalement en CAD, utiliser CAD
    if (totalMontantCAD > totalMontantMAD) {
      montantPrincipal = totalMontantCAD;
      monnaieAffichage = 'CAD';
    }
    
    const totalPayees = factures.filter(facture => facture.statut?.toLowerCase() === 'payee').length;
    const totalEnAttente = factures.filter(facture => ['brouillon', 'payable'].includes(facture.statut?.toLowerCase())).length;
    
    // 🔴 UTILISER LA LOGIQUE MÉTIER POUR CALCULER LES FACTURES EN RETARD
    const totalEnRetard = factures.filter(facture => estFactureEnRetard(facture)).length;
    
    console.log('📊 Statistiques calculées:', {
      totalMAD: totalMontantMAD.toFixed(2),
      totalCAD: totalMontantCAD.toFixed(2),
      monnaieAffichage,
      montantPrincipal: montantPrincipal.toFixed(2)
    });
    
    return {
      totalFactures,
      totalMontant: formatMontantDetaille(montantPrincipal, monnaieAffichage), // Afficher avec la bonne monnaie
      totalPayees,
      totalEnAttente,
      totalEnRetard
    };
  };
  
  const stats = calculerStatistiques();
  
  // 🔴 FONCTION POUR CHARGER LE LOGO EN BASE64 AVEC FALLBACK VISIBLE
  const loadLogoAsBase64 = () => {
    return new Promise((resolve) => {
      console.log('🔴 DEBUG: Début du chargement du logo');
      const img = new Image();
      let triedStaticFallback = false;
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        console.log('✅ DEBUG: Logo chargé avec succès, dimensions:', img.width, 'x', img.height);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // 🔴 NOUVEAU : Haute résolution pour éviter la pixellisation
        // Utiliser une résolution 3x plus élevée pour maintenir la qualité
        canvas.width = 300;  // 3x plus grand
        canvas.height = 150; // 3x plus grand
        
        // 🔴 AMÉLIORATION : Lissage et qualité d'image améliorée
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Dessiner l'image avec la haute résolution
        ctx.drawImage(img, 0, 0, 300, 150);
        
        // Convertir en base64 avec qualité maximale
        const dataURL = canvas.toDataURL('image/png', 1.0);
        console.log('✅ DEBUG: Logo converti en base64, taille:', dataURL.length, 'caractères');
        resolve(dataURL);
      };
      
      img.onerror = function() {
        // 1) Si le logo backend (/api/logo) n'existe pas (404), tenter le logo statique du frontend
        //    (disponible dans nginx: /assets/logo.png)
        if (!triedStaticFallback) {
          triedStaticFallback = true;
          const fallbackUrl = `/assets/logo.png?v=${Date.now()}`;
          console.warn('⚠️ DEBUG: Logo backend indisponible, fallback vers:', fallbackUrl);
          img.src = fallbackUrl;
          return;
        }

        console.warn('⚠️ DEBUG: Logo non trouvé, création d\'un logo de remplacement visible');
        // 🔴 NOUVEAU : Créer un logo de remplacement VISIBLE au lieu de null
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 300;
        canvas.height = 150;
        
        // Fond blanc avec bordure
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 300, 150);
        ctx.strokeStyle = '#d32f2f';
        ctx.lineWidth = 6;
        ctx.strokeRect(3, 3, 294, 144);
        
        // Texte IMMIGREAT visible
        ctx.fillStyle = '#d32f2f';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('IMMIGREAT', 150, 80);
        ctx.font = '14px Arial';
        ctx.fillText('SERVICES D\'IMMIGRATION', 150, 110);
        
        const dataURL = canvas.toDataURL('image/png', 1.0);
        console.log('✅ DEBUG: Logo de remplacement créé, taille:', dataURL.length, 'caractères');
        resolve(dataURL);
      };
      
      // Charger le logo depuis les paramètres de l'application
      const logoUrl = settingsService.getLogoUrl(true);
      console.log('🔴 DEBUG: URL du logo:', logoUrl);
      img.src = logoUrl;
    });
  };
  
  const isFacturePayee = (facture) => {
    const statut = String(facture?.statut || facture?.status || '').toLowerCase();
    return ['payee', 'payée', 'payé', 'paid'].includes(statut);
  };

  const loadCachetAsBase64 = () => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      canvas.getContext('2d').drawImage(image, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    image.onerror = reject;
    image.src = '/assets/cachet-immigreat.png';
  });

  const ajouterCachetFacturePayee = async (doc, facture, x, y) => {
    if (!isFacturePayee(facture)) return;

    try {
      const cachet = await loadCachetAsBase64();
      doc.addImage(cachet, 'PNG', x, y, 70, 38);
    } catch (error) {
      console.warn('Impossible d’ajouter le cachet de facture payée:', error);
    }
  };

  // Nouveau canevas de facture inspiré du modèle transmis par le client.
  // Les données métier existantes sont conservées et les montants restent en dirhams.
  const dessinerNouveauCanevasFacture = async (doc, facture) => {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 14;
    const teal = [31, 105, 122];
    const paleTeal = [235, 242, 244];
    const borderTeal = [143, 181, 186];
    const montantTTC = Number.parseFloat(facture.montant) || 0;
    const montantHT = montantTTC / 1.2;
    const tva = montantTTC - montantHT;
    const montant = (value) => `${value.toFixed(2)} DH`;
    const dateFacturation = formatDate(facture.dateEmission || facture.dateCreation || new Date());

    doc.setFillColor(...paleTeal);
    doc.ellipse(pageWidth - 4, -16, 105, 52, 'F');
    doc.ellipse(-20, pageHeight + 15, 90, 36, 'F');

    const logoBase64 = await loadLogoAsBase64();
    try {
      // Le logo source est carré : conserver ses proportions évite de l'écraser.
      doc.addImage(logoBase64, 'PNG', margin, 10, 35, 35);
    } catch (error) {
      doc.setTextColor(...teal);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(17);
      doc.text(settings.nom?.toUpperCase() || 'IMMIGREAT', margin, 27);
    }

    const companyX = pageWidth - margin;
    const addressLines = String(settings.adresse || 'Fès, Maroc')
      .split(',')
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 4);
    doc.setTextColor(20, 32, 48);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(settings.nom?.toUpperCase() || 'IMMIGREAT', companyX, 16, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    addressLines.forEach((line, index) => doc.text(line, companyX, 22 + (index * 5), { align: 'right' }));
    if (settings.telephone) {
      doc.text(`Tél. ${settings.telephone}`, companyX, 22 + (addressLines.length * 5), { align: 'right' });
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(facture.client || 'Client', margin, 58);

    doc.setTextColor(...teal);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(20);
    doc.text(`Facture ${getInvoiceDisplayNumber(facture)}`, pageWidth - margin, 76, { align: 'right' });

    doc.setFillColor(249, 251, 251);
    doc.setDrawColor(...borderTeal);
    doc.setLineWidth(0.6);
    doc.roundedRect(margin, 84, pageWidth - (margin * 2), 17, 3, 3, 'FD');
    doc.setTextColor(...borderTeal);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Date de facturation', margin + 3, 91);
    doc.setTextColor(20, 32, 48);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(dateFacturation, margin + 3, 97);

    const tableY = 108;
    const descriptionX = margin;
    const quantityX = 96;
    const unitX = 119;
    const taxX = 150;
    const amountX = 172;
    const tableRight = pageWidth - margin;
    doc.setFillColor(...teal);
    doc.roundedRect(margin, tableY, tableRight - margin, 10, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('DESCRIPTION', descriptionX + 2, tableY + 6.5);
    doc.text('QTÉ', quantityX + 1, tableY + 6.5);
    doc.text('PRIX UNITAIRE', unitX + 1, tableY + 6.5);
    doc.text('TVA', taxX + 2, tableY + 6.5);
    doc.text('MONTANT', tableRight - 2, tableY + 6.5, { align: 'right' });

    const descriptionLines = doc.splitTextToSize(
      facture.description || 'Service de consultation en immigration',
      quantityX - descriptionX - 5,
    );
    const rowHeight = Math.max(14, descriptionLines.length * 5 + 5);
    const rowY = tableY + 10;
    doc.setDrawColor(150, 157, 167);
    doc.setLineWidth(0.3);
    // Bordure unique arrondie pour l'en-tête et la ligne de service.
    doc.roundedRect(margin, tableY, tableRight - margin, 10 + rowHeight, 2, 2, 'S');
    doc.line(margin, rowY, tableRight, rowY);
    [quantityX, unitX, taxX, amountX].forEach((x) => doc.line(x, tableY, x, rowY + rowHeight));
    doc.setTextColor(20, 32, 48);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(descriptionLines, descriptionX + 2, rowY + 6);
    doc.text('1', quantityX + 3, rowY + 6);
    doc.text(montant(montantHT), taxX - 3, rowY + 6, { align: 'right' });
    doc.text('20%', amountX - 3, rowY + 6, { align: 'right' });
    doc.text(montant(montantHT), tableRight - 2, rowY + 6, { align: 'right' });

    const totalsX = 120;
    const totalsY = rowY + rowHeight;
    const totalsWidth = tableRight - totalsX;
    const totalRows = [
      ['Montant hors taxes', montantHT],
      ['TVA (20%)', tva],
      ['Total', montantTTC],
    ];
    totalRows.forEach(([label, value], index) => {
      const y = totalsY + (index * 9);
      const isTotal = index === totalRows.length - 1;
      doc.setFillColor(...(isTotal ? teal : [248, 249, 250]));
      doc.rect(totalsX, y, totalsWidth, 9, 'FD');
      doc.setTextColor(...(isTotal ? [255, 255, 255] : [20, 32, 48]));
      doc.setFont('helvetica', isTotal ? 'bold' : 'normal');
      doc.setFontSize(9.5);
      doc.text(label, totalsX + 2, y + 6);
      doc.text(montant(value), tableRight - 2, y + 6, { align: 'right' });
    });

    const detailsY = totalsY + 13;
    doc.setTextColor(20, 32, 48);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    if (facture.methodePaiement) doc.text(`Méthode de paiement : ${facture.methodePaiement}`, margin, detailsY);
    doc.text(`Statut de paiement : ${mapStatutForDisplay(facture.statut)}`, margin, detailsY + 7);
    await ajouterCachetFacturePayee(doc, facture, pageWidth - margin - 70, detailsY + 8);

    const footerY = pageHeight - 15;
    doc.setDrawColor(20, 32, 48);
    doc.setLineWidth(0.4);
    doc.line(margin, footerY - 7, pageWidth - margin, footerY - 7);
    doc.setTextColor(55, 65, 81);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text(
      `${settings.nom || 'IMMIGREAT'} | ICE : ${settings.ice_number || '003718833000089'} | IF : ${settings.if_number || '66984070'} | RC : ${settings.rc_number || '85737'}`,
      pageWidth / 2,
      footerY,
      { align: 'center' },
    );
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(7.5);
    doc.text('Page 1 / 1', pageWidth / 2, footerY + 7, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  };

  // Function to download an invoice as a PDF (FORMAT OFFICIEL)
  const telechargerFacture = async (facture) => {
    console.log('🔴 DEBUG: Début de la génération PDF pour téléchargement');
    console.log('🔴 DEBUG: Données de la facture:', JSON.stringify(facture, null, 2));
    const doc = new jsPDF({ compress: true, putOnlyUsedFonts: true });
    try {
      await drawModernInvoiceTemplate(doc, facture);
    } catch (error) {
      alert(error.message || 'Impossible de générer la facture.');
      return;
    }
    doc.save(getInvoicePdfFileName(facture));
    return;
    
    // 🔴 CHARGER LE LOGO HAUTE RÉSOLUTION
    const logoBase64 = await loadLogoAsBase64();
    console.log('🔴 DEBUG: Logo reçu pour PDF, présent:', !!logoBase64, 'taille:', logoBase64?.length || 0);
    
    // 🔴 CONFIGURATION DU DOCUMENT
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    
    // 🔴 EN-TÊTE OFFICIEL
    // Logo - haute résolution et taille BEAUCOUP PLUS GRANDE pour visibilité maximale
    // Maintenant logoBase64 contient toujours une image (vraie ou placeholder)
    try {
      // 🔴 NOUVEAU : Logo décalé vers la gauche (position 10 au lieu de 20)
      console.log('🔴 DEBUG: Ajout du logo au PDF, position (10, 8), taille (70, 55)');
      doc.addImage(logoBase64, 'PNG', 10, 8, 70, 55);
      console.log('✅ DEBUG: Logo ajouté avec succès au PDF');
    } catch (error) {
      console.error('❌ DEBUG: Erreur lors de l\'ajout du logo:', error);
      // Fallback final: rectangle avec bordure visible rouge IMMIGREAT (aussi décalé)
      doc.setFillColor(211, 47, 47); // Rouge IMMIGREAT
      doc.rect(10, 8, 70, 55, 'F');
      doc.setFillColor(255, 255, 255);
      doc.rect(12, 10, 66, 51, 'F');
      doc.setLineWidth(2);
      doc.setDrawColor(211, 47, 47);
      doc.rect(10, 8, 70, 55, 'S');
      doc.setFontSize(12);
      doc.setTextColor(211, 47, 47);
      doc.text('IMMIGREAT', 25, 35);
      doc.setFontSize(8);
      doc.text('SERVICES D\'IMMIGRATION', 18, 45);
      console.log('✅ DEBUG: Fallback logo dessiné');
    }
    
    // Informations de l'entreprise (décalées vers la gauche pour équilibre avec le logo)
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text(settings.nom?.toUpperCase() || 'IMMIGREAT', margin + 60, 25);
    
    // 🔴 NOUVEAU : Adresse formatée sur plusieurs lignes (position optimisée pour logo plus grand)
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    // Diviser l'adresse en parties logiques - Utiliser l'adresse des paramètres système
    const adresseComplete = settings.adresse || 'ESPACE LES CHAMPS 1-46, 1 LOT PRESTIGIA, CHAMPS DE COURSES, Fès, Maroc';
    const adresseParts = adresseComplete.split(',').map(part => part.trim());
    
    // Afficher chaque partie sur une ligne (position décalée vers la gauche)
    let adresseY = 33;
    adresseParts.forEach(part => {
      doc.text(part, margin + 60, adresseY);
      adresseY += 5;
    });
    
    doc.text(`Tél: ${settings.telephone || '+212 5356-54753'} | Email: ${settings.email || 'contact@immigreat.ma'}`, margin + 60, adresseY + 2);
    doc.text(`IF: ${settings.if_number || '66984070'} | ICE: ${settings.ice_number || '003718833000089'}`, margin + 60, adresseY + 8);
    doc.text(`RC: ${settings.rc_number || '85737'} | TP: ${settings.tp_number || '13604809'}`, margin + 60, adresseY + 14);
    
    // Ligne de séparation (position optimisée pour tout le contenu)
    doc.setLineWidth(0.5);
    doc.line(margin, 73, pageWidth - margin, 73);
    
    // 🔴 TITRE FACTURE (position ajustée pour tout le contenu)
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(41, 128, 185); // Bleu professionnel
    doc.text('FACTURE', pageWidth - margin - 50, 90);
    
    // 🔴 INFORMATIONS FACTURE ET CLIENT (position ajustée)
    let yPosition = 110;
    
    // Informations facture (côté droit)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Numéro de facture:', pageWidth - margin - 80, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(getInvoiceDisplayNumber(facture), pageWidth - margin - 20, yPosition);
    
    yPosition += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Date d\'émission:', pageWidth - margin - 80, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(facture.dateEmission), pageWidth - margin - 20, yPosition);
    
    yPosition += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Date d\'échéance:', pageWidth - margin - 80, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(facture.dateEcheance), pageWidth - margin - 20, yPosition);
    
console.log(" DEBUG: V�rification datePaiement:", facture.datePaiement);

    
    // Informations client (côté gauche) - position normale
    // Descendre légèrement ce bloc pour éviter le chevauchement avec le titre FACTURE
    yPosition = 100;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('FACTURÉ À:', margin, yPosition);

    // Afficher la date de paiement à côté si elle existe
    if (facture.datePaiement) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Date de paiement:', pageWidth - margin - 80, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(formatDate(facture.datePaiement), pageWidth - margin - 20, yPosition);
    }
    
    yPosition += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(facture.client, margin, yPosition);
    
    // 🔴 TABLEAU DES SERVICES
    yPosition += 30;
    
    // 🔥 DÉTERMINER LA MONNAIE ET LE SYMBOLE
    const monnaieFacture = facture.monnaie || 'MAD';
    const symboleMonnaie = monnaieFacture === 'MAD' ? 'DH' : '$';
    const tauxTVA = monnaieFacture === 'MAD' ? 1.2 : 1.15; // 20% pour MAD, 15% pour CAD
    const pourcentageTVA = monnaieFacture === 'MAD' ? '20%' : '15%';
    
    // 🔥 CALCULS CORRECTS : Le montant en base est déjà TTC
    const montantTTC = parseFloat(facture.montant) || 0;
    const montantHT = montantTTC / tauxTVA;
    const tva = montantTTC - montantHT;
    
    // En-tête du tableau
    doc.setFillColor(41, 128, 185);
    doc.rect(margin, yPosition, pageWidth - 2 * margin, 10, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('DESCRIPTION DU SERVICE', margin + 5, yPosition + 7);
    doc.text(`MONTANT (${monnaieFacture})`, pageWidth - margin - 40, yPosition + 7);
    
    yPosition += 15;
    
    // Contenu du tableau
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    
    // Service principal
    doc.text(facture.description || 'Service de consultation en immigration', margin + 5, yPosition);
    doc.text(`${montantHT.toFixed(2)} ${symboleMonnaie}`, pageWidth - margin - 40, yPosition);
    
    yPosition += 10;
    
    // Ligne de séparation
    doc.setLineWidth(0.3);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    
    // 🔴 CALCULS DES TOTAUX
    yPosition += 15;
    
    // Sous-total
    doc.setFont('helvetica', 'normal');
    doc.text('Sous-total (HT):', pageWidth - margin - 80, yPosition);
    doc.text(`${montantHT.toFixed(2)} ${symboleMonnaie}`, pageWidth - margin - 25, yPosition);
    
    yPosition += 8;
    doc.text(`TVA (${pourcentageTVA}):`, pageWidth - margin - 80, yPosition);
    doc.text(`${tva.toFixed(2)} ${symboleMonnaie}`, pageWidth - margin - 25, yPosition);
    
    yPosition += 8;
    
    // Total en gras
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('TOTAL À PAYER:', pageWidth - margin - 80, yPosition);
    doc.text(`${montantTTC.toFixed(2)} ${symboleMonnaie}`, pageWidth - margin - 35, yPosition);
    
    // Encadrer le total
    doc.setLineWidth(1);
    doc.rect(pageWidth - margin - 90, yPosition - 5, 85, 12);
    
    // 🔴 INFORMATIONS DE PAIEMENT
    yPosition += 25;
    
    if (facture.methodePaiement) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Méthode de paiement:', margin, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(facture.methodePaiement, margin + 50, yPosition);
      yPosition += 8;
    }
    
    doc.setFont('helvetica', 'bold');
    doc.text('Statut de paiement:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    const statutDisplay = mapStatutForDisplay(facture.statut);
    doc.text(statutDisplay, margin + 50, yPosition);
    await ajouterCachetFacturePayee(doc, facture, pageWidth - margin - 70, yPosition + 8);
    
    // 🔴 CONDITIONS DE PAIEMENT
    const footerY = pageHeight - 18;
    const footerLineY = footerY - 6;
    const footerSafeTop = footerLineY;
    const maxTextWidth = pageWidth - (margin * 2);
    const lineHeight = 5;
    const conditionsBullets = [
      "Paiement exigible dans les 30 jours suivant la date d'émission",
      "Des frais de retard de 1,5% par mois s'appliquent aux paiements en retard",
      'En cas de litige, les tribunaux de Montréal seront seuls compétents',
    ];

    const countLines = (text) => doc.splitTextToSize(text, maxTextWidth).length;
    const conditionsLinesCount = conditionsBullets.reduce((sum, bullet) => sum + countLines(`• ${bullet}`), 0);
    const conditionsBlockHeight = 20 + 8 + (conditionsLinesCount * lineHeight);
    if (yPosition + conditionsBlockHeight > footerSafeTop) {
      doc.addPage();
      yPosition = margin;
    }

    yPosition += 20;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('CONDITIONS DE PAIEMENT:', margin, yPosition);
    
    yPosition += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    for (const bullet of conditionsBullets) {
      const lines = doc.splitTextToSize(`• ${bullet}`, maxTextWidth);
      for (const line of lines) {
        doc.text(String(line), margin, yPosition);
        yPosition += lineHeight;
      }
      yPosition += 1;
    }
    
    // 🔴 PIED DE PAGE
    
    doc.setLineWidth(0.5);
    doc.line(margin, footerLineY, pageWidth - margin, footerLineY);
    
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(`Merci de votre confiance | ${settings.nom || 'Services d\'Immigration Expert'}`, margin, footerY);
    doc.text(`Facture générée le ${formatDate(new Date())}`, pageWidth - margin, footerY, { align: 'right' });
    
    // Télécharger le PDF
    doc.save(getInvoicePdfFileName(facture));
  };

  // Function to print an invoice as a PDF (FORMAT OFFICIEL)
  const imprimerFacture = async (facture) => {
    const doc = new jsPDF({ compress: true, putOnlyUsedFonts: true });
    try {
      await drawModernInvoiceTemplate(doc, facture);
    } catch (error) {
      alert(error.message || 'Impossible de générer la facture.');
      return;
    }
    window.open(doc.output('bloburl'), '_blank');
    return;
    
    // 🔴 CHARGER LE LOGO HAUTE RÉSOLUTION
    const logoBase64 = await loadLogoAsBase64();
    
    // 🔴 CONFIGURATION DU DOCUMENT
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    
    // 🔴 EN-TÊTE OFFICIEL
    // Logo - haute résolution et taille BEAUCOUP PLUS GRANDE pour visibilité maximale
    // Maintenant logoBase64 contient toujours une image (vraie ou placeholder)
    try {
      // 🔴 NOUVEAU : Logo décalé vers la gauche (position 10 au lieu de 20)
      doc.addImage(logoBase64, 'PNG', 10, 8, 70, 55);
    } catch (error) {
      console.error('Erreur lors de l\'ajout du logo:', error);
      // Fallback final: rectangle avec bordure visible rouge IMMIGREAT (aussi décalé)
      doc.setFillColor(211, 47, 47); // Rouge IMMIGREAT
      doc.rect(10, 8, 70, 55, 'F');
      doc.setFillColor(255, 255, 255);
      doc.rect(12, 10, 66, 51, 'F');
      doc.setLineWidth(2);
      doc.setDrawColor(211, 47, 47);
      doc.rect(10, 8, 70, 55, 'S');
      doc.setFontSize(12);
      doc.setTextColor(211, 47, 47);
      doc.text('IMMIGREAT', 25, 35);
      doc.setFontSize(8);
      doc.text('SERVICES D\'IMMIGRATION', 18, 45);
    }
    
    // Informations de l'entreprise (décalées vers la gauche pour équilibre avec le logo)
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text(settings.nom?.toUpperCase() || 'IMMIGREAT', margin + 60, 25);
    
    // 🔴 NOUVEAU : Adresse formatée sur plusieurs lignes (position optimisée pour logo plus grand)
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    // Diviser l'adresse en parties logiques - Utiliser l'adresse des paramètres système
    const adresseComplete = settings.adresse || 'ESPACE LES CHAMPS 1-46, 1 LOT PRESTIGIA, CHAMPS DE COURSES, Fès, Maroc';
    const adresseParts = adresseComplete.split(',').map(part => part.trim());
    
    // Afficher chaque partie sur une ligne (position décalée vers la gauche)
    let adresseY = 33;
    adresseParts.forEach(part => {
      doc.text(part, margin + 60, adresseY);
      adresseY += 5;
    });
    
    doc.text(`Tél: ${settings.telephone || '+212 5356-54753'} | Email: ${settings.email || 'contact@immigreat.ma'}`, margin + 60, adresseY + 2);
    doc.text(`IF: ${settings.if_number || '66984070'} | ICE: ${settings.ice_number || '003718833000089'}`, margin + 60, adresseY + 8);
    doc.text(`RC: ${settings.rc_number || '85737'} | TP: ${settings.tp_number || '13604809'}`, margin + 60, adresseY + 14);
    
    // Ligne de séparation (position optimisée pour tout le contenu)
    doc.setLineWidth(0.5);
    doc.line(margin, 73, pageWidth - margin, 73);
    
    // 🔴 TITRE FACTURE (position ajustée pour tout le contenu)
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(41, 128, 185); // Bleu professionnel
    doc.text('FACTURE', pageWidth - margin - 50, 90);
    
    // 🔴 INFORMATIONS FACTURE ET CLIENT (position ajustée)
    let yPosition = 110;
    
    // Informations facture (côté droit)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Numéro de facture:', pageWidth - margin - 80, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(getInvoiceDisplayNumber(facture), pageWidth - margin - 20, yPosition);
    
    yPosition += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Date d\'émission:', pageWidth - margin - 80, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(facture.dateEmission), pageWidth - margin - 20, yPosition);
    
    yPosition += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Date d\'échéance:', pageWidth - margin - 80, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(facture.dateEcheance), pageWidth - margin - 20, yPosition);
    
console.log(" DEBUG: V�rification datePaiement:", facture.datePaiement);

    
    // Informations client (côté gauche) - position normale
    // Descendre légèrement ce bloc pour éviter le chevauchement avec le titre FACTURE
    yPosition = 100;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('FACTURÉ À:', margin, yPosition);

    // Afficher la date de paiement à côté si elle existe
    if (facture.datePaiement) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Date de paiement:', pageWidth - margin - 80, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(formatDate(facture.datePaiement), pageWidth - margin - 20, yPosition);
    }
    
    yPosition += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(facture.client, margin, yPosition);
    
    // 🔴 TABLEAU DES SERVICES
    yPosition += 30;
    
    // 🔥 DÉTERMINER LA MONNAIE ET LE SYMBOLE
    const monnaieFacture = facture.monnaie || 'MAD';
    const symboleMonnaie = monnaieFacture === 'MAD' ? 'DH' : '$';
    const tauxTVA = monnaieFacture === 'MAD' ? 1.2 : 1.15; // 20% pour MAD, 15% pour CAD
    const pourcentageTVA = monnaieFacture === 'MAD' ? '20%' : '15%';
    
    // 🔥 CALCULS CORRECTS : Le montant en base est déjà TTC
    const montantTTC = parseFloat(facture.montant) || 0;
    const montantHT = montantTTC / tauxTVA;
    const tva = montantTTC - montantHT;
    
    // En-tête du tableau
    doc.setFillColor(41, 128, 185);
    doc.rect(margin, yPosition, pageWidth - 2 * margin, 10, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('DESCRIPTION DU SERVICE', margin + 5, yPosition + 7);
    doc.text(`MONTANT (${monnaieFacture})`, pageWidth - margin - 40, yPosition + 7);
    
    yPosition += 15;
    
    // Contenu du tableau
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    
    // Service principal
    doc.text(facture.description || 'Service de consultation en immigration', margin + 5, yPosition);
    doc.text(`${montantHT.toFixed(2)} ${symboleMonnaie}`, pageWidth - margin - 40, yPosition);
    
    yPosition += 10;
    
    // Ligne de séparation
    doc.setLineWidth(0.3);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    
    // 🔴 CALCULS DES TOTAUX
    yPosition += 15;
    
    // Sous-total
    doc.setFont('helvetica', 'normal');
    doc.text('Sous-total (HT):', pageWidth - margin - 80, yPosition);
    doc.text(`${montantHT.toFixed(2)} ${symboleMonnaie}`, pageWidth - margin - 25, yPosition);
    
    yPosition += 8;
    doc.text(`TVA (${pourcentageTVA}):`, pageWidth - margin - 80, yPosition);
    doc.text(`${tva.toFixed(2)} ${symboleMonnaie}`, pageWidth - margin - 25, yPosition);
    
    yPosition += 8;
    
    // Total en gras
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('TOTAL À PAYER:', pageWidth - margin - 80, yPosition);
    doc.text(`${montantTTC.toFixed(2)} ${symboleMonnaie}`, pageWidth - margin - 35, yPosition);
    
    // Encadrer le total
    doc.setLineWidth(1);
    doc.rect(pageWidth - margin - 90, yPosition - 5, 85, 12);
    
    // 🔴 INFORMATIONS DE PAIEMENT
    yPosition += 25;
    
    if (facture.methodePaiement) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Méthode de paiement:', margin, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(facture.methodePaiement, margin + 50, yPosition);
      yPosition += 8;
    }
    
    doc.setFont('helvetica', 'bold');
    doc.text('Statut de paiement:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    const statutDisplay = mapStatutForDisplay(facture.statut);
    doc.text(statutDisplay, margin + 50, yPosition);
    await ajouterCachetFacturePayee(doc, facture, pageWidth - margin - 70, yPosition + 8);
    
    // 🔴 CONDITIONS DE PAIEMENT (FLOW 2 - PRINT)
    const footerY2 = pageHeight - 18;
    const footerLineY2 = footerY2 - 6;
    const footerSafeTop2 = footerLineY2;
    const maxTextWidth2 = pageWidth - (margin * 2);
    const lineHeight2 = 5;
    const conditionsBullets2 = [
      "Paiement exigible dans les 30 jours suivant la date d'émission",
      "Des frais de retard de 1,5% par mois s'appliquent aux paiements en retard",
      'En cas de litige, les tribunaux de Montréal seront seuls compétents',
    ];

    const countLines2 = (text) => doc.splitTextToSize(text, maxTextWidth2).length;
    const conditionsLinesCount2 = conditionsBullets2.reduce((sum, bullet) => sum + countLines2(`• ${bullet}`), 0);
    const conditionsBlockHeight2 = 20 + 8 + (conditionsLinesCount2 * lineHeight2);
    if (yPosition + conditionsBlockHeight2 > footerSafeTop2) {
      doc.addPage();
      yPosition = margin;
    }

    yPosition += 20;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('CONDITIONS DE PAIEMENT:', margin, yPosition);
    
    yPosition += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    for (const bullet of conditionsBullets2) {
      const lines = doc.splitTextToSize(`• ${bullet}`, maxTextWidth2);
      for (const line of lines) {
        doc.text(String(line), margin, yPosition);
        yPosition += lineHeight2;
      }
      yPosition += 1;
    }
    
    // 🔴 PIED DE PAGE
    
    doc.setLineWidth(0.5);
    doc.line(margin, footerLineY2, pageWidth - margin, footerLineY2);
    
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(`Merci de votre confiance | ${settings.nom || 'Services d\'Immigration Expert'}`, margin, footerY2);
    doc.text(`Facture générée le ${formatDate(new Date())}`, pageWidth - margin, footerY2, { align: 'right' });
    
    // Ouvrir le PDF pour impression
    window.open(doc.output('bloburl'), '_blank');
  };
  
  // Fonction pour voir l'avoir d'une facture annulée
  const voirAvoir = async (facture) => {
    try {
      console.log('🔍 Recherche de l\'avoir pour la facture:', facture.id);
      const result = await getAvoirByFacture(facture.id);
      
      if (result.success && result.data) {
        console.log('✅ Avoir trouvé:', result.data);
        
        // Charger le logo en base64 pour l'avoir
        const logoBase64 = await loadLogoAsBase64();
        
        // Créer un objet settings avec le logo chargé
        const settingsAvecLogo = {
          ...settings,
          logoBase64: logoBase64  // Ajouter le logo en base64
        };
        
        // Ouvrir le PDF de l'avoir dans un nouvel onglet
        await openAvoirPdfInNewTab(result.data, settingsAvecLogo);
      } else {
        console.log('❌ Aucun avoir trouvé pour cette facture');
        setShowAvoirErrorModal(true);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la récupération de l\'avoir:', error);
      setShowAvoirErrorModal(true);
    }
  };
  
  // État pour gérer l'ouverture/fermeture des menus Actions (avec ancre pour PortalDropdown)
  const toggleDropdown = (id, event) => {
    if (dropdownOpenId === id) {
      setDropdownOpenId(null);
      setDropdownAnchorEl(null);
    } else {
      setDropdownOpenId(id);
      setDropdownAnchorEl(event?.currentTarget || null);
    }
  };
  
  const ouvrirEdition = (facture) => {
    console.log('🔧 Ouverture édition pour facture:', facture);
    
    // Extraire les prestations du prestations_details si disponible
    let prestations = [];
    if (facture.prestations_details) {
      try {
        prestations = JSON.parse(facture.prestations_details);
        console.log('📋 Prestations extraites:', prestations);
        // S'assurer que les montants sont numériques
        prestations = prestations.map(p => ({
          description: p.description || '',
          montant: parseFloat(p.montant) || 0
        }));
      } catch (error) {
        console.error('Erreur parsing prestations_details:', error);
        prestations = [{ description: facture.description || '', montant: parseFloat(facture.montant) || 0 }];
      }
    } else if (facture.prestations && Array.isArray(facture.prestations)) {
      prestations = facture.prestations.map(p => ({
        description: p.description || '',
        montant: parseFloat(p.montant) || 0
      }));
    } else {
      prestations = [{ description: facture.description || '', montant: parseFloat(facture.montant) || 0 }];
    }

    // Calculer les montants
    const montantHT = prestations.reduce((sum, p) => sum + (parseFloat(p.montant) || 0), 0);
    const tva = montantHT * 0.15;
    const montantTTC = montantHT + tva;

    console.log('💰 Montants calculés:', { montantHT, tva, montantTTC });

    // Enrichir la facture avec des prestations par défaut si manquantes
    const factureComplete = {
      ...facture,
      prestations,
      montantHT,
      tva,
      montantTTC,
      // S'assurer que la date est au bon format pour le formulaire
      date: facture.dateEmission || facture.date_creation?.split('T')[0] || getAppDateInputValue()
    };
    
    console.log('📝 Facture complète pour édition:', factureComplete);
    
    setFactureToEdit(factureComplete);
    setShowEditModal(true);
    setDropdownOpenId(null);
  };
  
  const handleSaveEdition = async (updatedFacture) => {
    try {
      console.log('📝 === DEBUT SAUVEGARDE FACTURE ===');
      console.log('📝 Facture reçue pour sauvegarde:', updatedFacture);
      
      // Extraire les données essentielles
      const { id, numero, date, prestations, ...restData } = updatedFacture;
      
      // 🔴 ETAPE 1: VALIDATION ET NETTOYAGE DES PRESTATIONS
      let prestationsValidees = [];
      if (Array.isArray(prestations) && prestations.length > 0) {
        prestationsValidees = prestations
          .filter(p => p && (p.description || p.montant)) // Filtrer les prestations vides
          .map(p => ({
            description: (p.description || '').trim() || 'Service de consultation',
            montant: parseFloat(p.montant) || 0
          }));
      }
      
      // Si aucune prestation valide, créer une prestation par défaut
      if (prestationsValidees.length === 0) {
        prestationsValidees = [{
          description: 'Service de consultation',
          montant: 0
        }];
      }
      
      console.log('📋 Prestations validées:', prestationsValidees);
      
      // 🔴 ETAPE 2: CALCUL DU MONTANT DEFINITIF (HT uniquement pour cohérence)
      const montantHT = prestationsValidees.reduce((total, prestation) => {
        return total + (parseFloat(prestation.montant) || 0);
      }, 0);
      
      // Arrondir à 2 décimales pour éviter les erreurs de flottant
      const montantFinal = Math.round(montantHT * 100) / 100;
      
      console.log('💰 === CALCULS MONTANTS ===');
      console.log('💰 Montant HT calculé:', montantHT);
      console.log('💰 Montant final (arrondi):', montantFinal);
      console.log('💰 TVA (15%):', Math.round(montantFinal * 0.15 * 100) / 100);
      console.log('💰 TTC:', Math.round(montantFinal * 1.15 * 100) / 100);
      
      // 🔴 ETAPE 3: MISE A JOUR DES PRESTATIONS SI NECESSAIRE
      // Si le montant total des prestations ne correspond pas au montant final
      // (peut arriver lors de modifications manuelles), on met à jour la première prestation
      const sommePrestations = prestationsValidees.reduce((sum, p) => sum + p.montant, 0);
      if (Math.abs(sommePrestations - montantFinal) > 0.01) {
        console.log('⚠️ Correction automatique des prestations');
        console.log(`⚠️ Somme prestations: ${sommePrestations}, Montant attendu: ${montantFinal}`);
        
        if (prestationsValidees.length > 0) {
          prestationsValidees[0].montant = montantFinal;
          prestationsValidees[0].description = `Service de consultation - ${montantFinal} CAD`;
        }
      }
      
      console.log('📋 Prestations finales:', prestationsValidees);
      
      // 🔴 ETAPE 4: PREPARATION DU PAYLOAD API
      const payload = {
        montant: montantFinal, // ⭐ MONTANT HT UNIQUEMENT
        dateEmission: date || getAppDateInputValue(),
        description: (prestationsValidees[0]?.description || 'Service de consultation'),
        statut: updatedFacture.statut || 'brouillon',
        prestations_details: JSON.stringify(prestationsValidees), // ⭐ PRESTATIONS COHERENTES
        dateValidation: new Date().toISOString(),
        validePar: userName || 'utilisateur',
        // Conserver les autres champs existants
        client: restData.client,
        client_id: restData.client_id,
        dateEcheance: restData.dateEcheance,
        datePaiement: restData.datePaiement,
        methodePaiement: restData.methodePaiement
      };
      
      console.log('📤 === PAYLOAD FINAL ===');
      console.log('📤 Payload à envoyer:', payload);
      console.log('📤 Montant final dans payload:', payload.montant);
      console.log('📤 Prestations_details:', payload.prestations_details);
      
      // 🔴 ETAPE 5: ENVOI A L'API
      const result = await updateFacture(id, payload);
      
      if (result.success) {
        console.log('✅ === SAUVEGARDE REUSSIE ===');
        console.log('✅ Facture mise à jour avec succès');
        console.log('✅ Montant sauvegardé:', payload.montant);
        
        // Recharger les données pour vérifier la cohérence
        await chargerFactures();
        setShowEditModal(false);
        setFactureToEdit(null);
        
        // Afficher le modal de succès moderne
        setSuccessModalData({
          numero: payload.numero,
          montant: payload.montant,
          monnaie: payload.monnaie || 'MAD'
        });
        setShowSuccessModal(true);
      } else {
        console.error('❌ Erreur API:', result.error);
        alert(`❌ Erreur lors de la mise à jour: ${result.error || 'Erreur inconnue'}`);
      }
      
    } catch (err) {
      console.error('❌ === ERREUR SAUVEGARDE ===');
      console.error('❌ Erreur lors de la sauvegarde:', err);
      alert('❌ Erreur lors de la mise à jour de la facture');
    }
  };
  
  // Gérer l'état de chargement
  if (loading) {
    return (
      <div className="facturation-container">
        <div className="loading-container">
          <h2>Chargement des factures...</h2>
          <div className="loading-spinner">⏳</div>
        </div>
      </div>
    );
  }

  // Variables pour la pagination et les statistiques
  const paginatedFactures = getPaginatedFactures();
  const totalPages = getTotalPages();
  
  // Debug pour la pagination
  console.log(`🔍 [FACTURATION] Pagination Debug - Total factures: ${factures.length}, Factures filtrées: ${getFilteredFactures().length}, Total pages: ${totalPages}, Page actuelle: ${currentPage}`);
  console.log(`🔍 [FACTURATION] Show pagination: ${totalPages > 1}`);

  return (
    <div className="facturation-container">
      <div className="facturation-header">
        <h2>Gestion de la Facturation</h2>
        {userRole === 'conseillere' && (
          <div className="filtrage-info">
            <span className="filtrage-badge" title={`Vous ne voyez que les factures où validePar = "${userName}" pour des raisons de confidentialité`}>
              🔒 Mes factures {filteredBy && filteredBy !== 'Toutes' ? `(${filteredBy})` : ''}
            </span>
          </div>
        )}
        {isFacturationReadOnly && (
          <div className="filtrage-info">
            <span className="filtrage-badge" title="Les secrétaires peuvent consulter toutes les factures et modifier les brouillons.">
              👁️ Toutes les factures — modification des brouillons autorisée
            </span>
          </div>
        )}
        <div className="header-actions">
          <button className="btn-secondary" onClick={chargerFactures} disabled={loading}>
            🔄 Actualiser
          </button>
          {!isFacturationReadOnly && (
            <button className="btn-primary" onClick={() => setShowModal(true)}>
              <span>+</span> Nouvelle Facture
            </button>
          )}
        </div>
      </div>
      
      {error && (
        <div className="error-container">
          <p className="error-message">❌ {error}</p>
          <button className="btn-secondary" onClick={() => setError(null)}>
            Fermer
          </button>
        </div>
      )}
      
      <div className="stats-container">
        <div className="stat-card">
          <h3>{userRole === 'conseillere' ? 'Mes factures' : 'Total des factures'}</h3>
          <p className="stat-value">{stats.totalFactures}</p>
        </div>
        <div className="stat-card">
          <h3>Montant total</h3>
          <p className="stat-value">{stats.totalMontant}</p>
        </div>
        <div className="stat-card">
          <h3>Factures payées</h3>
          <p className="stat-value">{stats.totalPayees}</p>
        </div>
        <div className="stat-card">
          <h3>En attente</h3>
          <p className="stat-value">{stats.totalEnAttente}</p>
        </div>
        <div className="stat-card">
          <h3>En retard</h3>
          <p className="stat-value stat-alert">{stats.totalEnRetard}</p>
        </div>
      </div>
      
      <div className="filtres-container">
        <div className="filtre-group">
          <label>Statut:</label>
          <select 
            name="statut" 
            value={filtres.statut} 
            onChange={handleFiltreChange}
          >
            <option value="">Tous</option>
            {statutOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        
        <div className="filtre-group">
          <label>Client:</label>
          <input 
            type="text"
            name="client" 
            value={filtres.client} 
            onChange={handleFiltreChange}
            placeholder="Tapez le nom du client..."
            autoComplete="off"
          />
        </div>
        
        <div className="filtre-group">
          <label>Période:</label>
          <select 
            name="periode" 
            value={filtres.periode} 
            onChange={handleFiltreChange}
          >
            <option value="">Toutes</option>
            {periodeOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        
        {/* 🔴 Filtre par conseillère (visible uniquement pour les administrateurs) */}
        {(userRole === 'admin' || userRole === 'administrateur') && (
          <div className="filtre-group">
            <label>Conseillère:</label>
            <select 
              name="conseillere" 
              value={filtres.conseillere} 
              onChange={handleFiltreChange}
            >
              <option value="">Toutes</option>
              {conseilleresListe.map(conseillere => (
                <option key={conseillere} value={conseillere}>{conseillere}</option>
              ))}
            </select>
          </div>
        )}
        
        <button className="btn-secondary" onClick={resetFiltres}>
          Réinitialiser
        </button>
      </div>
      
      {/* Pagination en haut */}
      {getTotalPages() > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '20px',
          padding: '15px 0',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <span style={{
            color: '#6c757d',
            fontSize: '14px',
            marginRight: '15px'
          }}>
            Page {currentPage} sur {getTotalPages()}
          </span>
          
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            style={{
              padding: '6px 12px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
              color: currentPage === 1 ? '#6c757d' : '#495057',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '12px'
            }}
          >
            ← Préc
          </button>
          
          <div style={{ display: 'flex', gap: '3px' }}>
            {Array.from({ length: Math.min(getTotalPages(), 7) }, (_, index) => {
              let page;
              if (getTotalPages() <= 7) {
                page = index + 1;
              } else if (currentPage <= 4) {
                page = index + 1;
              } else if (currentPage >= getTotalPages() - 3) {
                page = getTotalPages() - 6 + index;
              } else {
                page = currentPage - 3 + index;
              }
              
              return (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  style={{
                    padding: '6px 10px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: page === currentPage ? '#007bff' : 'white',
                    color: page === currentPage ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontWeight: page === currentPage ? 'bold' : 'normal',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                    fontSize: '12px'
                  }}
                >
                  {page}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={goToNextPage}
            disabled={currentPage === getTotalPages()}
            style={{
              padding: '6px 12px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: currentPage === getTotalPages() ? '#f8f9fa' : 'white',
              color: currentPage === getTotalPages() ? '#6c757d' : '#495057',
              cursor: currentPage === getTotalPages() ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '12px'
            }}
          >
            Suiv →
          </button>
          
          <span style={{
            color: '#6c757d',
            fontSize: '12px',
            marginLeft: '15px'
          }}>
            {getFilteredFactures().length} factures
          </span>
        </div>
      )}
      
      <div className="factures-table-container">
        <table className="factures-table">
          <thead>
            <tr>
              <th 
                onClick={() => handleSort('numero')}
                className={sortConfig.key === 'numero' ? 'sorted' : ''}
              >
                Numéro {getSortIcon('numero')}
              </th>
              <th 
                onClick={() => handleSort('client')}
                className={sortConfig.key === 'client' ? 'sorted' : ''}
              >
                Client {getSortIcon('client')}
              </th>
              <th 
                onClick={() => handleSort('montant')}
                className={sortConfig.key === 'montant' ? 'sorted' : ''}
              >
                Montant {getSortIcon('montant')}
              </th>
              <th 
                onClick={() => handleSort('statut')}
                className={sortConfig.key === 'statut' ? 'sorted' : ''}
              >
                Statut {getSortIcon('statut')}
              </th>
              <th 
                onClick={() => handleSort('dateEmission')}
                className={sortConfig.key === 'dateEmission' ? 'sorted' : ''}
              >
                Date émission {getSortIcon('dateEmission')}
              </th>
              <th 
                onClick={() => handleSort('dateEcheance')}
                className={sortConfig.key === 'dateEcheance' ? 'sorted' : ''}
              >
                Date échéance {getSortIcon('dateEcheance')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {facturesFiltrees.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {loading ? 'Chargement...' : 'Aucune facture trouvée'}
                </td>
              </tr>
            ) : (
              getPaginatedFactures().map(facture => (
                <tr key={facture.id} className={`${estFactureEnRetard(facture) ? 'facture-en-retard' : ''} ${facture.statut?.toLowerCase() === 'brouillon' ? 'facture-brouillon' : ''}`}>
                  <td>
                    {getInvoiceDisplayNumber(facture)}
                    {/* 🔴 NOUVEAU : Indicateur visuel pour les factures en brouillon */}
                    {facture.statut?.toLowerCase() === 'brouillon' && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', backgroundColor: '#ffc107', color: '#000', padding: '2px 6px', borderRadius: '3px' }}>
                        BROUILLON
                      </span>
                    )}
                  </td>
                  <td>{facture.client}</td>
                  <td>{formatMontantDetaille(facture.montant, facture.monnaie)}</td>
                  <td>
                    <span className={`statut-badge ${getStatutClass(facture.statut)}`}>
                      {mapStatutForDisplay(facture.statut)}
                    </span>
                  </td>
                  <td>{formatDate(facture.dateEmission)}</td>
                  <td>{formatDate(facture.dateEcheance)}</td>
                <td>
                  <div className="actions-dropdown">
                    <button 
                      className="btn-action actions-dropdown-button" 
                      onClick={(e) => toggleDropdown(facture.id, e)}
                      type="button"
                    >
                      Actions ▾
                    </button>
                    {dropdownOpenId === facture.id && (
                      <PortalDropdown
                        anchorEl={dropdownAnchorEl}
                        onClose={() => { setDropdownOpenId(null); setDropdownAnchorEl(null); }}
                        align="end"
                        offset={6}
                        autoFlip={true}
                        preferDown={true}
                      >
                        <div className="dropdown-content actions-dropdown-menu show" style={{ minWidth: 200 }}>
                          <button className="actions-dropdown-item" onClick={() => { voirDetails(facture); setDropdownOpenId(null); setDropdownAnchorEl(null); }}>
                            Voir détails
                          </button>
                          <button className="actions-dropdown-item" onClick={async () => { await telechargerFacture(facture); setDropdownOpenId(null); setDropdownAnchorEl(null); }}>
                            Télécharger
                          </button>
                          <button className="actions-dropdown-item" onClick={async () => { await imprimerFacture(facture); setDropdownOpenId(null); setDropdownAnchorEl(null); }}>
                            Imprimer
                          </button>
                          {facture.statut?.toLowerCase() === 'annulee' && (
                            <button className="actions-dropdown-item" onClick={async () => { await voirAvoir(facture); setDropdownOpenId(null); setDropdownAnchorEl(null); }} style={{ backgroundColor: '#dc2626', color: 'white' }}>
                              📄 Voir l'avoir
                            </button>
                          )}
                          {canEditFacture(facture.statut, userRole) && (
                            <button className="actions-dropdown-item" onClick={() => { ouvrirEdition(facture); setDropdownOpenId(null); setDropdownAnchorEl(null); }}>
                              ✏️ Modifier
                            </button>
                          )}
                          {!isFacturationReadOnly && facture.statut?.toLowerCase() === 'brouillon' && (
                            <button className="actions-dropdown-item" onClick={() => { changerStatut(facture.id, 'Payable'); setDropdownOpenId(null); setDropdownAnchorEl(null); }} style={{ backgroundColor: '#2196f3', color: 'white' }}>
                              📝 Rendre payable
                            </button>
                          )}
                          {canMarkAsPaid(facture.statut) && (
                            <button className="actions-dropdown-item" onClick={() => { changerStatut(facture.id, 'Payée', 'Carte de crédit'); setDropdownOpenId(null); setDropdownAnchorEl(null); }}>
                              Marquer comme payée
                            </button>
                          )}
                          {canEditFacture(facture.statut, userRole) && facture.statut?.toLowerCase() !== 'en_retard' && (
                            <button className="actions-dropdown-item" onClick={() => { changerStatut(facture.id, 'En retard'); setDropdownOpenId(null); setDropdownAnchorEl(null); }}>
                              Marquer en retard
                            </button>
                          )}
                          {!isFacturationReadOnly && facture.statut?.toLowerCase() !== 'annulee' && (
                            <button className="actions-dropdown-item danger" onClick={() => { changerStatut(facture.id, 'Annulée'); setDropdownOpenId(null); setDropdownAnchorEl(null); }}>
                              Annuler la facture
                            </button>
                          )}
                          {isSuperAdmin && (
                            <button
                              className="actions-dropdown-item danger"
                              onClick={() => supprimerFacture(facture)}
                              style={{ backgroundColor: '#991b1b', color: 'white' }}
                            >
                              Supprimer la facture
                            </button>
                          )}
                        </div>
                      </PortalDropdown>
                    )}
                  </div>
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination en bas */}
      {getTotalPages() > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginTop: '20px',
          padding: '20px 0'
        }}>
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
              color: currentPage === 1 ? '#6c757d' : '#495057',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            ← Précédent
          </button>
          
          <div style={{ display: 'flex', gap: '5px' }}>
            {Array.from({ length: Math.min(getTotalPages(), 5) }, (_, index) => {
              let page;
              if (getTotalPages() <= 5) {
                page = index + 1;
              } else if (currentPage <= 3) {
                page = index + 1;
              } else if (currentPage >= getTotalPages() - 2) {
                page = getTotalPages() - 4 + index;
              } else {
                page = currentPage - 2 + index;
              }
              
              return (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: page === currentPage ? '#007bff' : 'white',
                    color: page === currentPage ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontWeight: page === currentPage ? 'bold' : 'normal',
                    transition: 'all 0.2s ease',
                    minWidth: '40px'
                  }}
                >
                  {page}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={goToNextPage}
            disabled={currentPage === getTotalPages()}
            style={{
              padding: '8px 16px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: currentPage === getTotalPages() ? '#f8f9fa' : 'white',
              color: currentPage === getTotalPages() ? '#6c757d' : '#495057',
              cursor: currentPage === getTotalPages() ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Suivant →
          </button>
        </div>
      )}
      
      {/* Informations de pagination */}
      <div style={{
        textAlign: 'center',
        color: '#6c757d',
        fontSize: '14px',
        marginTop: '10px'
      }}>
        Affichage {((currentPage - 1) * facturesPerPage) + 1} à {Math.min(currentPage * facturesPerPage, getFilteredFactures().length)} sur {getFilteredFactures().length} factures
      </div>
      
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Créer une nouvelle facture</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={ajouterFacture}>
              <div className="form-row">
                <div className="form-group">
                  <label>Client:</label>
                  <ClientAutocomplete
                    value={nouvelleFacture.client}
                    onSelect={handleClientSelect}
                    placeholder="Rechercher un client par nom ou téléphone..."
                    required
                  />
                  {nouvelleFacture.selectedClientId && (
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      ✅ Client sélectionné (ID: {nouvelleFacture.selectedClientId})
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Montant TTC:</label>
                  <input 
                    type="number" 
                    name="montant" 
                    value={nouvelleFacture.montant} 
                    onChange={handleNouvelleFactureChange} 
                    step="0.01"
                    min="0"
                    required 
                  />
                  {nouvelleFacture.montant && (
                    <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                      {formatMontantAvecDetails(nouvelleFacture.montant, nouvelleFacture.monnaie)}
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Monnaie:</label>
                  <select 
                    name="monnaie" 
                    value={nouvelleFacture.monnaie} 
                    onChange={handleNouvelleFactureChange}
                    required
                  >
                    <option value="MAD">MAD (Dirham Marocain)</option>
                    <option value="CAD">CAD (Dollar Canadien)</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Date d'émission:</label>
                  <input 
                    type="date" 
                    name="dateEmission" 
                    value={nouvelleFacture.dateEmission} 
                    onChange={handleDateEmissionChange} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Date d'échéance:</label>
                  <input 
                    type="date" 
                    name="dateEcheance" 
                    value={nouvelleFacture.dateEcheance} 
                    onChange={handleNouvelleFactureChange} 
                    required 
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label>Date de paiement (facultative):</label>
                  <input
                    type="date"
                    name="datePaiement"
                    value={nouvelleFacture.datePaiement || ''}
                    onChange={handleNouvelleFactureChange}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Description:</label>
                  <textarea 
                    name="description" 
                    value={nouvelleFacture.description} 
                    onChange={handleNouvelleFactureChange} 
                    required
                  ></textarea>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showDetailsModal && factureDetails && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Détails de la facture</h3>
              <button className="close-btn" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="facture-details">
              <div className="facture-info">
                <h4>Informations générales</h4>
                <p><strong>Numéro:</strong> {getInvoiceDisplayNumber(factureDetails)}</p>
                <p><strong>Client:</strong> {factureDetails.client}</p>
                <p><strong>Montant:</strong> {formatMontantAvecDetails(factureDetails.montant, factureDetails.monnaie)}</p>
                <p><strong>Statut:</strong> 
                  <span className={`statut-badge statut-${factureDetails.statut.toLowerCase().replace(/\s+/g, '-')}`}>
                    {factureDetails.statut}
                  </span>
                </p>
                <p><strong>Date d'émission:</strong> {factureDetails.dateEmission}</p>
                <p><strong>Date d'échéance:</strong> {factureDetails.dateEcheance}</p>
                {factureDetails.datePaiement && (
                  <p><strong>Date de paiement:</strong> {factureDetails.datePaiement}</p>
                )}
                {factureDetails.methodePaiement && (
                  <p><strong>Méthode de paiement:</strong> {factureDetails.methodePaiement}</p>
                )}
                <p><strong>Description:</strong> {factureDetails.description}</p>
              </div>
              
              {factureDetails.statut !== 'Payée' && factureDetails.statut !== 'Annulée' && (
                <div className="facture-actions">
                  <h4>Actions</h4>
                  <div className="paiement-form">
                    {canMarkAsPaid(factureDetails.statut) && (
                      <>
                        <h5>Marquer comme payée</h5>
                        <div className="form-group">
                          <label>Méthode de paiement:</label>
                          <select 
                            id="methodePaiement" 
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) {
                                console.log('🔄 Tentative de changement de statut avec méthode:', e.target.value);
                                changerStatut(factureDetails.id, 'Payée', e.target.value);
                              }
                            }}
                          >
                            <option value="">Sélectionner une méthode</option>
                            {methodePaiementOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                    
                    <div className="paiement-actions">
                      <button 
                        className="btn-primary" 
                        onClick={() => {
                          setShowDetailsModal(false);
                          setFactureDetails(null);
                        }}
                        style={{ marginTop: '10px' }}
                      >
                        FERMER
                      </button>
                    </div>
                    
                    <div className="other-actions">
                      {!isFacturationReadOnly && factureDetails.statut !== 'En retard' && (
                        <button 
                          className="btn-secondary" 
                          onClick={() => changerStatut(factureDetails.id, 'En retard')}
                        >
                          Marquer en retard
                        </button>
                      )}
                      {!isFacturationReadOnly && (
                        <button 
                          className="btn-secondary" 
                          onClick={() => changerStatut(factureDetails.id, 'Annulée')}
                        >
                          Annuler la facture
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              {isSuperAdmin && (
                <button
                  className="btn-secondary"
                  onClick={() => supprimerFacture(factureDetails)}
                  style={{ backgroundColor: '#991b1b', color: 'white' }}
                >
                  Supprimer la facture
                </button>
              )}
              <button className="btn-primary" onClick={() => setShowDetailsModal(false)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal d'édition */}
      {showEditModal && factureToEdit && (
        <div className="modal-overlay">
          <div className="modal-content">
            <FactureForm
              client={{ 
                nom: factureToEdit.client,
                prenom: '',
                email: '',
                telephone: '',
                numeroDossier: factureToEdit.numero 
              }}
              userRole={userRole}
              existingFacture={factureToEdit}
              onCancel={() => { setShowEditModal(false); setFactureToEdit(null); }}
              onSave={handleSaveEdition}
            />
          </div>
        </div>
      )}
      
      {/* Modal de succès moderne */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            padding: '0',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            animation: 'slideInScale 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
            overflow: 'hidden'
          }}>
            {/* Header avec icône */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '30px',
              textAlign: 'center',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                animation: 'pulse 2s infinite'
              }}>
                <span style={{ fontSize: '40px' }}>✅</span>
              </div>
              <h2 style={{
                color: 'white',
                fontSize: '28px',
                fontWeight: '700',
                margin: '0 0 10px 0',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
              }}>
                Succès !
              </h2>
              <p style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '16px',
                margin: 0
              }}>
                Facture mise à jour avec succès
              </p>
            </div>
            
            {/* Contenu */}
            <div style={{
              background: 'white',
              padding: '30px',
              color: '#1e293b'
            }}>
              <div style={{
                background: '#f8fafc',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                  paddingBottom: '12px',
                  borderBottom: '2px dashed #e2e8f0'
                }}>
                  <span style={{
                    color: '#64748b',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Numéro de facture
                  </span>
                  <span style={{
                    color: '#1e293b',
                    fontSize: '16px',
                    fontWeight: '700',
                    fontFamily: 'monospace'
                  }}>
                    {successModalData.numero}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{
                    color: '#64748b',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Montant (HT)
                  </span>
                  <span style={{
                    color: '#10b981',
                    fontSize: '20px',
                    fontWeight: '700'
                  }}>
                    {successModalData.montant} {getSymboleMonnaie(successModalData.monnaie)}
                  </span>
                </div>
              </div>
              
              <button
                onClick={() => setShowSuccessModal(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal d'erreur pour l'avoir */}
      {showAvoirErrorModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: '20px',
            padding: '0',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            animation: 'slideInScale 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
            overflow: 'hidden'
          }}>
            {/* Header avec icône */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '30px',
              textAlign: 'center',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                animation: 'pulse 2s infinite'
              }}>
                <span style={{ fontSize: '40px' }}>⚠️</span>
              </div>
              <h2 style={{
                color: 'white',
                fontSize: '28px',
                fontWeight: '700',
                margin: '0 0 10px 0',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
              }}>
                Aucun avoir trouvé
              </h2>
              <p style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '16px',
                margin: 0
              }}>
                Cette facture n'a pas d'avoir associé
              </p>
            </div>
            
            {/* Contenu */}
            <div style={{
              background: 'white',
              padding: '30px',
              color: '#1e293b'
            }}>
              <div style={{
                background: '#fef2f2',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                border: '1px solid #fecaca'
              }}>
                <p style={{
                  color: '#991b1b',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  margin: '0',
                  textAlign: 'center'
                }}>
                  Aucun avoir n'a été trouvé pour cette facture. 
                  <br />
                  Veuillez contacter un administrateur.
                </p>
              </div>
              
              <button
                onClick={() => setShowAvoirErrorModal(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.6)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Facturation;





